<template>
  <button
    class="switch-button"
    :class="buttonData.classes"
    @click="() => buttonData.action()"
  >
    <q-icon size="16px" :name="buttonData.icon" />

    <c-tooltip v-if="!buttonData.disabled">{{ buttonData.tooltip }} </c-tooltip>
  </button>
</template>

<script setup>
import { computed } from "vue";

const model = defineModel({
  type: Object,
  required: true,
  default: () => ({}),
});

const props = defineProps({
  switchType: {
    type: String,
    default: "period",
    validator: (value) => {
      // The value must match one of these strings
      return ["period", "year"].includes(value);
    },
  },
  switchDirection: {
    type: String,
    default: "previous",
    validator: (value) => {
      // The value must match one of these strings
      return ["previous", "next"].includes(value);
    },
  },
  disable: {
    type: Boolean,
    default: false,
  },
});

const buttonData = computed(() => {
  const { switchDirection, switchType, disable } = props;
  let disabled;

  const hasNext =
    switchType === "period"
      ? model.value.hasNextPeriod.value
      : model.value.hasNextSchoolYear.value;
  const hasPrevious =
    switchType === "period"
      ? model.value.hasPreviousPeriod.value
      : model.value.hasPreviousSchoolYear.value;

  const action = (canPerformAction) => {
    switch (switchType) {
      case "period":
        return (
          canPerformAction && model.value.changeSelectedPeriod(switchDirection)
        );
      case "year":
        return (
          canPerformAction &&
          model.value.changeSelectedSchoolYear(switchDirection)
        );
    }
  };

  switch (switchDirection) {
    case "previous":
      disabled = disable || !hasPrevious;

      return {
        icon: "fas fa-caret-left",
        classes: [...(disabled ? ["--disabled"] : []), "--previous"],
        tooltip:
          switchType === "period" ? "Vorige periode" : "Vorig schooljaar",
        disabled,
        action: () => action(!disabled),
      };
    case "next":
      disabled = disable || !hasNext;

      return {
        icon: "fas fa-caret-right",
        classes: [...(disabled ? ["--disabled"] : []), "--next"],
        tooltip:
          switchType === "period" ? "Volgende periode" : "Volgend schooljaar",
        disabled,
        action: () => action(!disabled),
      };
  }
});
</script>

<style lang="scss" scoped>
.switch-button {
  @apply tw-flex tw-items-center tw-border-slate-200 tw-p-2 tw-transition;

  &:not(.--disabled) {
    @apply tw-cursor-pointer;

    &:hover {
      @apply tw-bg-slate-100;
    }
  }

  &.--disabled {
    @apply tw-cursor-not-allowed tw-opacity-60;
  }

  &.--previous {
    @apply tw-rounded-l tw-border-r;
  }

  &.--next {
    @apply tw-rounded-r tw-border-l;
  }
}
</style>
